/**
 * HighlightPopover — small floating pill that appears near a text selection.
 * Provides a quick "Note" action to save the selection as a highlight note,
 * and a dismiss button. The actual text editing happens in NotesDockPanel.
 */
import React, { useEffect, useRef, useState } from 'react';

const PREVIEW_MAX = 60;

export default function HighlightPopover({
  rect,
  selectedText,
  sceneId,
  tutorialId,
  domain,
  onSave,
  onDismiss,
}) {
  const savingRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Dismiss on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onDismiss(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onDismiss]);

  if (!rect) return null;

  // position:fixed → use viewport coords only (do NOT add scrollY)
  const top = Math.max(8, rect.top - 56);
  const left = rect.left + rect.width / 2;

  const preview = selectedText.length > PREVIEW_MAX
    ? selectedText.slice(0, PREVIEW_MAX) + '…'
    : selectedText;

  const handleSave = async (e) => {
    // Prefer mousedown so we run before the browser clears the selection /
    // before a competing mouseup can unmount this popover.
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (savingRef.current || busy) return;
    if (!sceneId) {
      setError('No scene — try again on this lesson.');
      return;
    }
    savingRef.current = true;
    setBusy(true);
    setError('');
    try {
      await onSave({
        note_type: 'highlight',
        anchor_type: 'text_selection',
        anchor_data: { selected_text: selectedText },
        text: selectedText,
        scene_id: sceneId,
        tutorial_id: tutorialId,
        domain,
      });
    } catch (err) {
      setError(err?.message || 'Could not save note');
      savingRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div
      data-highlight-popover="true"
      style={{
        position: 'fixed',
        top,
        left,
        transform: 'translateX(-50%)',
        zIndex: 9990,
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 8,
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '8px 10px',
        gap: 6,
        minWidth: 180,
        maxWidth: 280,
        pointerEvents: 'auto',
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Arrow */}
      <div style={{
        position: 'absolute',
        bottom: -7,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 12,
        height: 7,
        overflow: 'hidden',
      }}>
        <div style={{
          width: 10,
          height: 10,
          background: '#1e293b',
          border: '1px solid #334155',
          transform: 'rotate(45deg)',
          marginTop: -5,
          marginLeft: 1,
        }} />
      </div>

      {/* Quote preview */}
      <span style={{
        fontSize: 11,
        color: '#94a3b8',
        fontStyle: 'italic',
        lineHeight: 1.4,
        borderLeft: '2px solid #fbbf24',
        paddingLeft: 6,
        maxWidth: '100%',
        wordBreak: 'break-word',
      }}>
        "{preview}"
      </span>

      {error && (
        <span style={{ fontSize: 11, color: '#fca5a5', lineHeight: 1.3 }}>
          {error}
        </span>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', width: '100%' }}>
        <button
          type="button"
          onMouseDown={handleSave}
          onClick={handleSave}
          disabled={busy}
          style={{
            flex: 1,
            background: '#fbbf24',
            color: '#1e293b',
            border: 'none',
            borderRadius: 5,
            padding: '4px 10px',
            fontSize: 12,
            fontWeight: 700,
            cursor: busy ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Saving…' : '🖊 Note'}
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(); }}
          onClick={onDismiss}
          style={{
            background: 'transparent',
            color: '#64748b',
            border: '1px solid #334155',
            borderRadius: 5,
            padding: '4px 8px',
            fontSize: 12,
            cursor: 'pointer',
            lineHeight: 1,
          }}
          aria-label="Dismiss highlight popover"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
